<template>
  <div class="main bg-clr--lighter-gray o-section--huge ff-invitation-page">
    <div class="ff-invitation-card bg-clr--white u-box-shadow">
      <div class="ff-invitation-head" v-if="invitation && invitation.clubLogo">
        <img :src="invitation.clubLogo" :alt="invitation.club || ''" />
      </div>

      <h1 class="ff-invitation-title">{{ t("ffInvitationTitle") }}</h1>

      <div v-if="loading" class="ff-invitation-loading">
        <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
      </div>

      <template v-else-if="state === 'invalid'">
        <p class="ff-invitation-message">{{ t("ffInvitationInvalid") }}</p>
        <p v-if="!token" class="ff-invitation-message">
          {{ t("ffInvitationMissingToken") }}
        </p>
        <a class="button is-light" href="/">{{ t("ffInvitationLoginCta") }}</a>
      </template>

      <template v-else-if="state === 'expired'">
        <p class="ff-invitation-message">{{ t("ffInvitationExpired") }}</p>
        <a class="button is-light" href="/">{{ t("ffInvitationLoginCta") }}</a>
      </template>

      <template v-else-if="state === 'revoked'">
        <p class="ff-invitation-message">{{ t("ffInvitationRevoked") }}</p>
        <a class="button is-light" href="/">{{ t("ffInvitationLoginCta") }}</a>
      </template>

      <template v-else-if="state === 'accepted'">
        <h2 class="ff-invitation-subtitle">
          {{ t("ffInvitationAcceptedTitle") }}
        </h2>
        <p class="ff-invitation-message" v-if="alreadyAccepted">
          {{ t("ffInvitationAlreadyAccepted") }}
        </p>
        <p class="ff-invitation-message">{{ t("ffInvitationAcceptedMsg") }}</p>
        <p class="ff-invitation-meta" v-if="invitation.fechaAceptacion">
          {{ formatDate(invitation.fechaAceptacion) }}
        </p>
        <a class="button is-primary" href="/">{{ t("ffInvitationLoginCta") }}</a>
      </template>

      <template v-else>
        <p class="ff-invitation-message">{{ t("ffInvitationIntro") }}</p>

        <div class="ff-invitation-details">
          <p class="ff-invitation-name">{{ invitation.nombre }}</p>
          <p v-if="invitation.invitante">
            <strong>{{ t("ffInvitationInvitedBy") }}:</strong>
            {{ invitation.invitante }}
          </p>
          <p v-if="invitation.membresia">
            <strong>{{ t("ffInvitationMembership") }}:</strong>
            {{ invitation.membresia }}
          </p>
          <p v-if="invitation.fechaExpiracion">
            <strong>{{ t("ffInvitationExpires") }}:</strong>
            {{ formatDate(invitation.fechaExpiracion) }}
          </p>
        </div>

        <label class="ff-invitation-check">
          <input type="checkbox" v-model="acceptAge" :disabled="submitting" />
          <span>{{ t("ffInvitationAgeCheck") }}</span>
        </label>

        <p class="ff-invitation-error" v-if="errorMessage">{{ errorMessage }}</p>

        <button
          class="button is-primary ff-invitation-submit"
          type="button"
          :disabled="!acceptAge || submitting"
          :class="{ 'is-loading': submitting }"
          @click="accept"
        >
          {{ submitting ? t("ffInvitationAccepting") : t("ffInvitationAccept") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import i18nData from "../utils/i18n_data";

export default {
  name: "FFInvitation",
  data() {
    return {
      token: "",
      loading: true,
      invitation: null,
      lang: "es",
      acceptAge: false,
      submitting: false,
      accepted: false,
      alreadyAccepted: false,
      errorCode: null,
      errorMessage: null,
    };
  },
  computed: {
    state() {
      if (this.accepted) return "accepted";
      if (this.errorCode === "FF_INVITATION_EXPIRED") return "expired";
      if (this.errorCode === "FF_INVITATION_REVOKED") return "revoked";
      if (this.errorCode) return "invalid";
      if (!this.invitation) return "invalid";

      const estado = this.invitation.estado;
      if (estado === "aceptada") return "accepted";
      if (estado === "expirada") return "expired";
      if (estado === "revocada") return "revoked";
      return "pending";
    },
  },
  mounted() {
    this.token = this.$route.query.token || "";
    this.load();
  },
  methods: {
    t(key) {
      const entry = i18nData[key];
      if (!entry) return key;
      return entry[this.lang] || entry.es || key;
    },
    formatDate(value) {
      if (!value) return "";
      return this.$moment(value).locale(this.lang).format("LL");
    },
    async load() {
      this.loading = true;
      this.errorCode = null;
      this.errorMessage = null;

      if (!this.token) {
        this.errorCode = "FF_INVITATION_MISSING_TOKEN";
        this.loading = false;
        return;
      }

      try {
        const { data } = await this.$axios.get(
          `/portal/friends-family/invitation/${encodeURIComponent(this.token)}`
        );
        this.invitation = data.data;
        this.lang = data.data.iso === "en" ? "en" : "es";
        this.alreadyAccepted = data.data.estado === "aceptada";
      } catch (error) {
        this.errorCode =
          error?.response?.data?.code || "FF_INVITATION_INVALID";
      } finally {
        this.loading = false;
      }
    },
    async accept() {
      if (!this.acceptAge) {
        this.errorMessage = this.t("ffInvitationAgeRequired");
        return;
      }

      this.errorMessage = null;
      this.submitting = true;

      try {
        const { data } = await this.$axios.post(
          `/portal/friends-family/invitation/${encodeURIComponent(
            this.token
          )}/accept`,
          { acceptAge: true }
        );
        this.alreadyAccepted = data?.data?.alreadyAccepted === true;
        this.invitation = {
          ...(this.invitation || {}),
          ...(data?.data || {}),
          estado: "aceptada",
          fechaAceptacion:
            data?.data?.fechaAceptacion || this.$moment().valueOf(),
        };
        this.accepted = true;
      } catch (error) {
        const code = error?.response?.data?.code;
        if (
          code === "FF_INVITATION_EXPIRED" ||
          code === "FF_INVITATION_REVOKED"
        ) {
          this.errorCode = code;
        } else {
          this.errorMessage =
            error?.response?.data?.message || this.t("ffInvitationError");
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.ff-invitation-page {
  min-height: 100vh;
  padding: 40px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.ff-invitation-card {
  width: 100%;
  max-width: 520px;
  border-radius: 12px;
  padding: 32px 28px;
}

.ff-invitation-head {
  text-align: center;
  margin-bottom: 20px;
}

.ff-invitation-head img {
  max-height: 56px;
  max-width: 220px;
}

.ff-invitation-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 16px 0;
  text-align: center;
}

.ff-invitation-subtitle {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
  text-align: center;
}

.ff-invitation-message {
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.ff-invitation-details {
  background: #f7f9fb;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 18px;
  font-size: 14px;
  line-height: 1.7;
}

.ff-invitation-details p {
  margin: 0;
}

.ff-invitation-name {
  font-size: 16px;
  font-weight: 600;
}

.ff-invitation-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  margin-bottom: 14px;
  cursor: pointer;
}

.ff-invitation-check input {
  margin-top: 4px;
}

.ff-invitation-error {
  color: #d32f2f;
  font-size: 13px;
  margin: 0 0 12px 0;
}

.ff-invitation-submit {
  width: 100%;
}

.ff-invitation-loading {
  text-align: center;
  padding: 24px 0;
  font-size: 22px;
}
</style>
